import { memo, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSession } from '@/hooks/use-session';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MessagesSquare, ArrowRight, Loader2, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface ChatHistoryItem {
  id: string;
  question: string;
  created_at: string;
  user_id: string;
}

interface StrykrChatHistoryCardProps {
  className?: string;
}

// Function to fetch recent chat history
const fetchRecentChatHistory = async (userId: string, limit = 5) => {
  const { data, error } = await supabase
    .from('chat_history')
    .select('id, question, created_at, user_id')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching recent chat history:', error);
    throw new Error('Failed to fetch recent chat history.');
  }
  return data || [];
};

const StrykrChatHistoryCard = memo(({ className }: StrykrChatHistoryCardProps) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [shouldLoadHistory, setShouldLoadHistory] = useState(false);

  // Delay loading chat history to prioritize alerts and calendar
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoadHistory(true);
    }, 1200); // Load after 1.2 seconds (after alerts and calendar)
    return () => clearTimeout(timer);
  }, []);

  const { 
    data: history = [], 
    isLoading, 
    error, 
    refetch 
  } = useQuery<ChatHistoryItem[]>({ // Explicitly type the query data
    queryKey: ['recent-chat-history', session?.user?.id],
    queryFn: () => {
      if (!session?.user?.id) return Promise.resolve([]); // Return empty array if no user
      return fetchRecentChatHistory(session.user.id);
    },
    enabled: !!session?.user?.id && shouldLoadHistory, // Only run query if user is logged in AND after delay
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
  });

  // Setup real-time subscription
  useEffect(() => {
    if (!session?.user?.id) return;

    const channel = supabase.channel('chat-history-changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_history', filter: `user_id=eq.${session.user.id}` },
        (payload) => {
          console.log('New chat history item:', payload.new);
          // Refetch or invalidate the query to update the list
          queryClient.invalidateQueries({ queryKey: ['recent-chat-history', session.user.id] });
          toast({
            title: "New Chat Saved",
            description: "Your latest conversation has been added to your history.",
          })
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('Subscribed to chat history changes');
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('Subscription error:', status, err);
        }
      });

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
      console.log('Unsubscribed from chat history changes');
    };
  }, [session?.user?.id, queryClient, toast]);

  const handleViewAllClick = () => {
    navigate('/chat'); // Navigate to chat with integrated history sidebar
  };

  const handleItemClick = (itemId: string) => {
    // Navigate to chat page with specific conversation ID
    console.log(`Navigate to chat with conversation: ${itemId}`);
    navigate(`/chat?conversationId=${itemId}`);
  };

  return (
    <Card className={cn("border-white/10 bg-black/30 backdrop-blur-sm h-full flex flex-col", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessagesSquare className="h-5 w-5 text-green-400" />
            <CardTitle className="text-lg font-bold text-white">Chat History</CardTitle>
          </div>
          {history.length > 0 && (
            <Button variant="ghost" size="sm" className="text-green-400 hover:text-green-300" onClick={handleViewAllClick}>
              View All
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
        <CardDescription>
          Your recent conversations with Strykr AI
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-grow overflow-y-auto pt-0 pb-4 space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full rounded-md bg-white/10" />
          ))
        ) : error ? (
          <div className="text-center text-red-400 py-4">
            <p>Error loading chat history.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
              Retry
            </Button>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-6 text-white/60">
            <MessageCircle className="h-10 w-10 mx-auto mb-2 text-white/30" />
            <p>No recent chats found.</p>
            <Button variant="link" className="text-green-400 mt-1" onClick={() => navigate('/chat')}>
              Start a new chat
            </Button>
          </div>
        ) : (
          history.map((item) => (
            <Button
              key={item.id}
              variant="outline"
              className="w-full justify-between items-center text-left h-auto py-2 px-3 border-white/10 bg-white/5 hover:bg-white/10 text-white/80 hover:text-white overflow-hidden"
              onClick={() => handleItemClick(item.id)}
            >
              <div className="flex-1 mr-2 min-w-0 overflow-hidden"> {/* min-width: 0 is crucial for flex child truncation */}
                <p className="text-sm font-medium truncate max-w-full">{item.question}</p>
                <p className="text-xs text-white/50 mt-0.5 truncate">
                  {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-green-400 flex-shrink-0 ml-1" />
            </Button>
          ))
        )}
      </CardContent>
    </Card>
  );
});

StrykrChatHistoryCard.displayName = 'StrykrChatHistoryCard';

// Maintain original name export for compatibility if needed
export { StrykrChatHistoryCard as RecentConversationsCard };
export default StrykrChatHistoryCard;
